/**
 * External dependencies
 */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Box,
  Stack,
  Heading,
  Text,
} from "@towersystems/towersystems-design-system";
import classnames from "classnames";
import { findIndex, memoize, create } from "lodash";
import { get, find } from "lodash";
import AsyncSelect from "react-select/async";
import debounce from "debounce-promise";
/**
 * Internal dependencies
 */
import DropdownLink from "components/dropdown-link";
import Filter from "./filter";
import Button from "components/button";
import ButtonGroup from "components/button-group";
import { getFilters, countFilters } from "state/ui/dashboard-filter/selectors";
import { setFilters } from "state/ui/dashboard-filter/actions";
import Icon from "components/icon";
import cubejsApi from "lib/cube-js";

/**
 * Style dependencies
 */
import "./styles.scss";

const createFetchOptionsFn = (name) =>
  debounce((query, callback) => {
    return cubejsApi
      .load({
        dimensions: [name],
        measures: [],
        timeDimensions: [],
        filters: [
          {
            member: name,
            operator: "contains",
            values: [query ?? ""],
          },
        ],
        limit: 50,
      })
      .then((result) => {
        const response = result.pivot().map((item) => {
          return {
            value: item["xValues"][0],
            label: item["xValues"][0],
          };
        });
        callback(response);
        return response;
      })
      .catch((err) => {
        callback([]);
      });
  }, 300);

const dashboardFn = createFetchOptionsFn("Department.name");
const supplierFn = createFetchOptionsFn("Supplier.name");
const categoryFn = createFetchOptionsFn("Category.name");
const productFn = createFetchOptionsFn("Product.name");

const DashboardFilters = ({ count, filters: defaultFilters, ...props }) => {
  const [filters, setFilters] = useState(defaultFilters || []);

  useEffect(() => {
    resetFilters(defaultFilters);
  }, [defaultFilters]);

  const applyFilters = () => {
    props.setFilters(filters);
  };

  const classes = classnames("dashboard__filters");

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const createHandleOnChangeFn = React.useCallback(
    (name) =>
      memoize((selectedOptions) => {
        let updatedFilters = [...filters];
        let index = findIndex(filters, { member: name });
        const values = selectedOptions.map((s) => s.value);

        if (index < 0) {
          updatedFilters = [
            ...updatedFilters,
            { member: name, operator: "contains", values },
          ];
        } else {
          updatedFilters[index] = {
            member: name,
            operator: "contains",
            values,
          };
        }

        setFilters(updatedFilters);
      }),
    []
  );

  return (
    <Box className={classes}>
      <DropdownLink
        title={
          <Box display="flex" alignItems="center">
            <Icon name="RiFilterLine" marginRight={5} />
            <Box>Filter deck {count >= 1 && ` (${count})`}</Box>
          </Box>
        }
        className="dashboard__filter-dropdown-link"
        onClose={resetFilters}
      >
        {({ close }) => (
          <Box padding="large">
            <Box marginBottom="large">
              <Heading level="3">Filter by</Heading>
            </Box>
            <Stack space="large" padding="large">
              <Box>
                <Text size="small" weight="bold">
                  Department
                </Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <AsyncSelect
                    isMulti
                    name="Department.name"
                    loadOptions={dashboardFn}
                    defaultOptions
                    placeholder="Search.."
                    onChange={createHandleOnChangeFn("Department.name")}
                    value={get(
                      find(filters, { member: "Department.name" }),
                      "values"
                    )?.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </Box>
              <Box>
                <Text size="small" weight="bold">
                  Category
                </Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <AsyncSelect
                    isMulti
                    name="Category.name"
                    placeholder="Search.."
                    defaultOptions
                    loadOptions={categoryFn}
                    onChange={createHandleOnChangeFn("Category.name")}
                    value={get(
                      find(filters, { member: "Category.name" }),
                      "values"
                    )?.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </Box>
              <Box>
                <Text size="small" weight="bold">
                  Supplier
                </Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    placeholder="Search.."
                    name="Supplier.name"
                    loadOptions={supplierFn}
                    defaultOptions
                    onChange={createHandleOnChangeFn("Supplier.name")}
                    value={get(
                      find(filters, { member: "Supplier.name" }),
                      "values"
                    )?.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </Box>
              <Box>
                <Text size="small" weight="bold">
                  Product
                </Text>
                <div onClick={(e) => e.stopPropagation()}>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    name="Product.name"
                    placeholder="Search.."
                    loadOptions={productFn}
                    defaultOptions
                    onChange={createHandleOnChangeFn("Product.name")}
                    value={get(
                      find(filters, { member: "Product.name" }),
                      "values"
                    )?.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </Box>
            </Stack>
            <ButtonGroup>
              <Button
                onClick={() => {
                  applyFilters();
                  close();
                }}
                fullWidth
                primary
              >
                Apply
              </Button>
              <Button
                onClick={() => {
                  close();
                }}
                fullWidth
              >
                Cancel
              </Button>
            </ButtonGroup>
            <Box marginTop="large">
              <Text size="xsmall">
                Filters will only apply to applicable reports.
              </Text>
            </Box>
            <Box marginTop="medium">
              <Text size="xsmall">
                Filters set here will overrride filters set on individual
                reports.
              </Text>
            </Box>
          </Box>
        )}
      </DropdownLink>
    </Box>
  );
};

export default connect(
  (state) => ({
    filters: getFilters(state),
    count: countFilters(state),
  }),
  { setFilters }
)(DashboardFilters);
